import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HEADCOUNT_BANDS, type HeadcountBand } from '@besideu/shared/signup';
import {
  UpdateOrgProfileSchema,
  type HrSettingsResponse,
  type UpdateOrgProfileRequest,
} from '@besideu/shared/hr-settings';
import { api } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { translateSaveError } from '@/lib/settings-errors.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';
import { PoolSourceBadge, type PoolSource } from '@/components/ui/PoolSourceBadge.tsx';

const POOL_SOURCE_COPY: Record<PoolSource, { label: string; title: string }> = {
  override: { label: 'Custom', title: 'A custom value has been set for your organisation.' },
  env: { label: 'Default', title: 'Using the platform default.' },
  fallback: { label: 'Fallback', title: 'Using the built-in fallback.' },
};

const EMPTY_FORM: UpdateOrgProfileRequest = {
  name: '',
  headcountBand: '1-9',
  primaryContact: { name: '', email: '' },
  escalationContact: { name: '', email: '' },
};

export function HrSettings() {
  const session = useSession('hr-shell');
  const isAdmin = session.role === 'HR_Admin';
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['hr', 'settings'],
    queryFn: () => api.get<HrSettingsResponse>('/api/hr/settings'),
  });

  const [form, setForm] = useState<UpdateOrgProfileRequest>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Seed the form from the query result exactly once, on first load — not on
  // every `settings.data` identity change. `save`'s `onSuccess` below calls
  // `invalidateQueries`, which forces a refetch; re-seeding on that refetch
  // would silently overwrite whatever the user has typed since clicking
  // Save (a real race, since inputs stay editable through the whole
  // PATCH-then-GET round trip).
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (!settings.data || hasSeeded.current) return;
    hasSeeded.current = true;
    const { organization } = settings.data;
    setForm({
      name: organization.name,
      headcountBand: organization.headcountBand ?? '1-9',
      primaryContact: organization.primaryContact ?? { name: '', email: '' },
      escalationContact: organization.escalationContact ?? { name: '', email: '' },
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (input: UpdateOrgProfileRequest) =>
      api.patch<{ ok: true }>('/api/hr/settings', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'settings'] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = UpdateOrgProfileSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        if (!next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    save.mutate(parsed.data);
  }

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-2 text-muted-foreground">
          Your organisation's profile and privacy configuration.
        </p>
      </header>

      {settings.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : settings.error ? (
        <p className="text-traffic-red">Could not load settings.</p>
      ) : (
        <>
          <form
            onSubmit={handleSubmit}
            className="max-w-2xl space-y-6 rounded-lg border border-border p-6"
          >
            <div>
              <h2 className="text-lg font-semibold">Organisation profile</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Captured when your organisation signed up.{' '}
                {isAdmin
                  ? 'Update it here if anything has changed.'
                  : 'Only an HR Admin can edit these details.'}
              </p>
            </div>

            <Field label="Organisation name" error={fieldErrors.name}>
              {(id, descProps) => (
                <TextInput
                  id={id}
                  {...descProps}
                  required
                  disabled={!isAdmin}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </Field>

            <Field label="Headcount" error={fieldErrors.headcountBand}>
              {(id, descProps) => (
                <Select
                  id={id}
                  {...descProps}
                  disabled={!isAdmin}
                  value={form.headcountBand}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, headcountBand: e.target.value as HeadcountBand }))
                  }
                >
                  {HEADCOUNT_BANDS.map((band) => (
                    <option key={band} value={band}>
                      {band}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Primary contact name" error={fieldErrors['primaryContact.name']}>
                {(id, descProps) => (
                  <TextInput
                    id={id}
                    {...descProps}
                    required
                    disabled={!isAdmin}
                    value={form.primaryContact.name}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        primaryContact: { ...f.primaryContact, name: e.target.value },
                      }))
                    }
                  />
                )}
              </Field>
              <Field label="Primary contact email" error={fieldErrors['primaryContact.email']}>
                {(id, descProps) => (
                  <TextInput
                    id={id}
                    {...descProps}
                    type="email"
                    required
                    disabled={!isAdmin}
                    value={form.primaryContact.email}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        primaryContact: { ...f.primaryContact, email: e.target.value },
                      }))
                    }
                  />
                )}
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Escalation contact name" error={fieldErrors['escalationContact.name']}>
                {(id, descProps) => (
                  <TextInput
                    id={id}
                    {...descProps}
                    required
                    disabled={!isAdmin}
                    value={form.escalationContact.name}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        escalationContact: { ...f.escalationContact, name: e.target.value },
                      }))
                    }
                  />
                )}
              </Field>
              <Field
                label="Escalation contact email"
                error={fieldErrors['escalationContact.email']}
              >
                {(id, descProps) => (
                  <TextInput
                    id={id}
                    {...descProps}
                    type="email"
                    required
                    disabled={!isAdmin}
                    value={form.escalationContact.email}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        escalationContact: { ...f.escalationContact, email: e.target.value },
                      }))
                    }
                  />
                )}
              </Field>
            </div>

            {isAdmin ? (
              <div className="flex flex-col items-start gap-1">
                <Button type="submit" loading={save.isPending}>
                  Save changes
                </Button>
                {save.isError ? (
                  <p className="text-sm text-traffic-red" role="alert">
                    {translateSaveError(save.error)}
                  </p>
                ) : save.isSuccess ? (
                  <p className="text-sm text-muted-foreground">Saved.</p>
                ) : null}
              </div>
            ) : null}
          </form>

          <PrivacySection privacy={settings.data.privacy} />
        </>
      )}
    </section>
  );
}

// Read-only — the source badge itself is shared with `OrgSettingsBlock` in
// `AdminOrgDetail.tsx` via `PoolSourceBadge`; there's no edit affordance
// here since changing this threshold stays an admin-only action.
function PrivacySection({ privacy }: { privacy: HrSettingsResponse['privacy'] }) {
  const { effective, source } = privacy.amberAggregationMinPool;
  return (
    <div className="max-w-2xl rounded-lg border border-border p-6">
      <h2 className="text-lg font-semibold">Privacy &amp; aggregation</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Aggregated wellbeing signals (Amber/Red) only surface on your dashboard once at least this
        many support pathways are present, to protect anonymity in small teams. To change this,
        contact besideU support.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <span>
          Minimum pool size: <span className="font-mono font-medium">{effective}</span>
        </span>
        <PoolSourceBadge source={source} copy={POOL_SOURCE_COPY} />
      </div>
    </div>
  );
}
